import React from "react";
import {useTranslation} from "react-i18next";
import {Checkbox, FormControlLabel, FormHelperText, Tooltip} from "@mui/material";
import {CustomStyleRadio, CustomStyleCheckbox, CustomStyleFormHelperText} from "./Styles";
import {IconCheckboxChecked, IconCheckboxUnchecked} from "./buttons/IconButtons";
/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * CustomCheckbox is a reusable checkbox component with support for custom icons, tooltips, helper text,
 * styling, and translation. It wraps MUI Checkbox and FormControlLabel.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Identifier for the checkbox (required)
 * @param {string} [props.name] - Name attribute for the checkbox (optional)
 * @param {boolean} [props.disabled=false] - Whether the checkbox is disabled
 * @param {string} [props.trans=''] - Text key for the label, supports i18n translation
 * @param {boolean} props.checked - Whether the checkbox is checked (required)
 * @param {Object} [props.inputProps] - Additional props applied to the native checkbox input
 * @param {string} [props.className] - Extra CSS classes for the wrapper
 * @param {string} [props.size="small"] - Size of the checkbox
 * @param {any} [props.value=null] - Value attribute of the checkbox
 * @param {number|boolean} [props.fontSize=false] - Font size of the label in pixels
 * @param {string} [props.tooltip=''] - Text for the tooltip displayed on hover
 * @param {string} [props.helperText=''] - Text displayed below the checkbox
 * @param {string} [props.bold="normal"] - Font weight for the label
 * @param {function} props.onChange - Change event handler (required)
 * @param {boolean} [props.indeterminate] - If true, renders the checkbox in an indeterminate state
 * @param {JSX.Element|null} [props.icon] - Custom icon for unchecked state
 * @param {JSX.Element|null} [props.checkedIcon] - Custom icon for checked state
 * @param {boolean} [props.disableRipple=true] - Disables the ripple effect on the checkbox
 *
 * @returns {JSX.Element} The rendered CustomCheckbox component
 *
 * @example
 * <CustomCheckbox
 *   id="agree"
 *   trans="I agree to terms"
 *   checked={checked}
 *   onChange={handleChange}
 *   tooltip="Check to agree"
 *   icon={<IconCheckboxUnchecked />}
 *   checkedIcon={<IconCheckboxChecked />}
 * />
 */
export const CustomCheckbox = (props) => {
    const {id, name,disabled=false, trans='',marginbutton=false, checked, inputProps, className, size="small",
        value= null, fontSize=false, tooltip='', helperText='', bold="normal", onChange,indeterminate,
        icon=<IconCheckboxUnchecked className={"icon-color-general-950"}/>,
        checkedIcon=<IconCheckboxChecked className={"icon-color-shades-800"}/>, disableRipple=false} = props;

    const { t } = useTranslation();

    const renderIcon = (component) => {
        return React.cloneElement(component, {
            className: disabled ? "icon-color-shades-400" : component.props.className
        });
    };

    let attr = {
        id:id,
        labelId:`${id}_label_id`,
        label:`${id}_label`,
        name:name,
        className:`checkbox ${className}`,
        checked:checked,
        onChange:onChange,
        indeterminate: indeterminate,
        size:size,
        disabled:disabled,
        inputProps:{...inputProps, ...props},
        value:value,
        sx:CustomStyleRadio,
        disableRipple:disableRipple,
        icon:renderIcon(icon),
        checkedIcon:renderIcon(checkedIcon)
    }

    if(value === null) delete attr.value;

    return <>
        <FormControlLabel className={className} sx={CustomStyleCheckbox(fontSize, trans, bold ,marginbutton)} disabled={disabled}
            control={
                <Tooltip
                    title={t(tooltip)}
                    placement="top"
                >
                    <Checkbox  {...attr} />
                </Tooltip>
            }
            label={
                <>
                    {t(`${trans}`)}
                    {helperText && <FormHelperText className={"css-k4qjio-MuiFormHelperText-root"} sx={{...CustomStyleFormHelperText(false), marginBottom: "0 !important"}}>{helperText}</FormHelperText>}
                </>
            }
        />
    </>
}
